import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Modal from "@/components/Modal";

/**
 * Roles & Permissions management panel (admin-only).
 * Talks to /api/roles + /api/roles/permissions-catalog.
 *
 * Strictly independent of existing modules. Enforcement of permissions on
 * existing endpoints is NOT wired in this sprint by explicit user request.
 */
export default function RolesPanel({ onBack }) {
  const { t } = useI18n();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ modules: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role obj or {}
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rr, cc] = await Promise.all([
        axios.get(`${API}/roles`, { withCredentials: true }),
        axios.get(`${API}/roles/permissions-catalog`, { withCredentials: true }),
      ]);
      setRoles(rr.data);
      setCatalog(cc.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/roles/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/roles`, payload, { withCredentials: true });
      }
      toast.success(t("role_save_ok"));
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const duplicate = async (id) => {
    try {
      await axios.post(`${API}/roles/${id}/duplicate`, {}, { withCredentials: true });
      toast.success(t("role_duplicated"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (role) => {
    if (!window.confirm(t("role_delete_confirm"))) return;
    try {
      await axios.delete(`${API}/roles/${role.id}`, { withCredentials: true });
      toast.success(t("delete"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid={DASHBOARD.rolesRoot} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-overline mb-1">RBAC / 04</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
            {t("roles_title")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
            {t("roles_subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={DASHBOARD.rolesBackBtn}
            onClick={onBack}
            className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
          >
            ← {t("back")}
          </button>
          <button
            data-testid={DASHBOARD.rolesNewBtn}
            onClick={() => {
              setEditing({});
              setOpen(true);
            }}
            className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
          >
            + {t("roles_new_btn")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : roles.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          {t("roles_none")}
        </div>
      ) : (
        <div
          data-testid={DASHBOARD.rolesList}
          className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]"
        >
          {roles.map((r) => (
            <div
              key={r.id}
              data-testid={DASHBOARD.roleRow(r.id)}
              className="grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)]"
            >
              <div className="col-span-12 md:col-span-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-lg font-bold tracking-tight">
                    {r.name}
                  </span>
                  {r.is_admin && (
                    <span className="font-mono-tech text-[0.6rem] font-bold tracking-[0.2em] px-1.5 py-0.5 bg-black text-white">
                      {t("role_badge_admin")}
                    </span>
                  )}
                  {r.is_system && !r.is_admin && (
                    <span className="font-mono-tech text-[0.6rem] font-bold tracking-[0.2em] px-1.5 py-0.5 bg-[var(--border)] text-[var(--text-secondary)]">
                      {t("role_badge_system")}
                    </span>
                  )}
                </div>
                {r.description ? (
                  <div className="text-sm text-[var(--text-secondary)] mt-1">
                    {r.description}
                  </div>
                ) : null}
              </div>
              <div className="col-span-6 md:col-span-3 text-sm text-[var(--text-secondary)] font-mono-tech">
                {Object.keys(r.permissions || {}).length} {t("role_perms_count")}
              </div>
              <div className="col-span-6 md:col-span-4 flex justify-end gap-2 flex-wrap">
                <button
                  data-testid={DASHBOARD.roleEditBtn(r.id)}
                  onClick={() => {
                    setEditing(r);
                    setOpen(true);
                  }}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                >
                  {t("role_edit")}
                </button>
                <button
                  data-testid={DASHBOARD.roleDuplicateBtn(r.id)}
                  onClick={() => duplicate(r.id)}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                >
                  {t("role_duplicate")}
                </button>
                <button
                  data-testid={DASHBOARD.roleDeleteBtn(r.id)}
                  onClick={() => remove(r)}
                  disabled={r.is_admin || r.is_system}
                  className="border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-3 py-1.5 text-sm hover:bg-[var(--alert-overdue)] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[var(--alert-overdue)]"
                >
                  {t("role_delete")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? t("role_edit") : t("roles_new_btn")}
        testId={DASHBOARD.roleFormModal}
        xwide
      >
        <RoleForm
          initial={editing}
          catalog={catalog}
          onSubmit={submit}
          onCancel={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      </Modal>
    </div>
  );
}

// ─── Role form (with permissions matrix) ───────────────────────────────
function RoleForm({ initial, catalog, onSubmit, onCancel }) {
  const { t } = useI18n();
  const isAdminRole = !!initial?.is_admin;
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [perms, setPerms] = useState(() => {
    // Initialise from initial.permissions or all-empty
    const base = {};
    for (const m of catalog.modules) base[m] = new Set(initial?.permissions?.[m] || []);
    return base;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Re-hydrate when the modal is opened with a different role
    const base = {};
    for (const m of catalog.modules) base[m] = new Set(initial?.permissions?.[m] || []);
    setPerms(base);
    setName(initial?.name || "");
    setDescription(initial?.description || "");
  }, [initial, catalog.modules]);

  const toggle = (module, action) => {
    if (isAdminRole) return;
    setPerms((prev) => {
      const next = { ...prev };
      const s = new Set(next[module] || []);
      if (s.has(action)) s.delete(action);
      else s.add(action);
      next[module] = s;
      return next;
    });
  };

  const toggleAll = (module) => {
    if (isAdminRole) return;
    setPerms((prev) => {
      const next = { ...prev };
      const s = new Set(next[module] || []);
      const isAll = catalog.actions.every((a) => s.has(a));
      next[module] = new Set(isAll ? [] : catalog.actions);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t("name_required") || "Nombre requerido");
      return;
    }
    setSaving(true);
    try {
      const permissions = {};
      for (const [m, s] of Object.entries(perms)) {
        const arr = Array.from(s);
        if (arr.length) permissions[m] = arr;
      }
      await onSubmit({ name: name.trim(), description: description.trim(), permissions });
    } finally {
      setSaving(false);
    }
  };

  const input =
    "w-full border border-[var(--border)] bg-white px-3 py-2 focus:border-[var(--primary)]";

  return (
    <form onSubmit={submit} className="space-y-5">
      {isAdminRole && (
        <div className="border border-black bg-black text-white px-4 py-2 text-sm font-mono-tech">
          {t("role_form_admin_note")}
        </div>
      )}
      {initial?.is_system && !isAdminRole && (
        <div className="border border-[var(--border)] bg-[var(--bg)] text-[var(--text-secondary)] px-4 py-2 text-sm">
          {t("role_form_system_note")}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-overline block mb-1">{t("role_name")}</label>
          <input
            data-testid={DASHBOARD.roleFormName}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={input}
          />
        </div>
        <div>
          <label className="label-overline block mb-1">{t("role_description")}</label>
          <input
            data-testid={DASHBOARD.roleFormDescription}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={input}
            placeholder="—"
          />
        </div>
      </div>

      <div>
        <label className="label-overline block mb-2">{t("role_permissions")}</label>
        <div className="border border-[var(--border)] bg-white overflow-auto max-h-[55vh]">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-[var(--bg)] z-10">
              <tr>
                <th className="text-left px-3 py-2 font-mono-tech text-xs tracking-wider border-b border-[var(--border)]">
                  MODULE
                </th>
                {catalog.actions.map((a) => (
                  <th
                    key={a}
                    className="px-2 py-2 font-mono-tech text-xs tracking-wider border-b border-[var(--border)] text-center whitespace-nowrap"
                  >
                    {t(`perm_act_${a}`) || a}
                  </th>
                ))}
                <th className="px-2 py-2 font-mono-tech text-xs tracking-wider border-b border-[var(--border)] text-center">
                  ALL
                </th>
              </tr>
            </thead>
            <tbody>
              {catalog.modules.map((m) => {
                const set = perms[m] || new Set();
                const isAll = catalog.actions.every((a) => set.has(a));
                return (
                  <tr key={m} className="border-b border-[var(--border)] hover:bg-[var(--bg)]">
                    <td className="px-3 py-2 font-display font-semibold whitespace-nowrap">
                      {t(`perm_mod_${m}`) || m}
                    </td>
                    {catalog.actions.map((a) => (
                      <td key={a} className="text-center px-2 py-2">
                        <input
                          type="checkbox"
                          data-testid={DASHBOARD.rolePermCheckbox(m, a)}
                          checked={isAdminRole || set.has(a)}
                          disabled={isAdminRole}
                          onChange={() => toggle(m, a)}
                          className="w-4 h-4 accent-black"
                        />
                      </td>
                    ))}
                    <td className="text-center px-2 py-2">
                      <input
                        type="checkbox"
                        data-testid={DASHBOARD.rolePermModuleAll(m)}
                        checked={isAdminRole || isAll}
                        disabled={isAdminRole}
                        onChange={() => toggleAll(m)}
                        className="w-4 h-4 accent-black"
                        title={t("perm_module_all")}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          data-testid={DASHBOARD.roleFormCancel}
          type="button"
          onClick={onCancel}
          className="flex-1 border border-[var(--border)] px-4 py-2.5 font-display font-medium hover:bg-[var(--surface-alt)]"
        >
          {t("cancel")}
        </button>
        <button
          data-testid={DASHBOARD.roleFormSubmit}
          type="submit"
          disabled={saving}
          className="flex-1 bg-[var(--text-primary)] text-white px-4 py-2.5 font-display font-bold hover:bg-[var(--primary)] disabled:opacity-50"
        >
          {saving ? "..." : t("save")}
        </button>
      </div>
    </form>
  );
}
